import React from 'react';
import { Button } from '@/components/ui/button';
import { Eye, X, Clock, User, FileText, ClipboardList } from 'lucide-react';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { formatDistanceToNow } from 'date-fns';

export const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonatedUser, endImpersonation, impersonationStartedAt } = useImpersonation();

  if (!isImpersonating || !impersonatedUser) return null;

  // Use window.location for navigation since this component may render outside Router
  const navigateTo = (path: string) => {
    window.location.href = path;
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Eye className="h-5 w-5" />
          <span className="font-medium">
            Viewing as: {impersonatedUser.display_name || impersonatedUser.email}
          </span>
          {impersonationStartedAt && (
            <span className="text-amber-800 text-sm flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Started {formatDistanceToNow(impersonationStartedAt, { addSuffix: true })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Quick navigation buttons */}
          <div className="hidden md:flex items-center gap-1 mr-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateTo('/profile')}
              className="text-amber-950 hover:text-amber-950 hover:bg-amber-600/20 h-7 px-2"
            >
              <User className="h-3 w-3 mr-1" />
              Profile
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateTo('/tps-profile')}
              className="text-amber-950 hover:text-amber-950 hover:bg-amber-600/20 h-7 px-2"
            >
              <FileText className="h-3 w-3 mr-1" />
              TPS
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateTo('/assessments')}
              className="text-amber-950 hover:text-amber-950 hover:bg-amber-600/20 h-7 px-2"
            >
              <ClipboardList className="h-3 w-3 mr-1" />
              Assessments
            </Button>
          </div>
          <span className="text-xs text-amber-800 hidden sm:inline">
            Read-only mode • No actions will be taken
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={endImpersonation}
            className="text-amber-950 hover:text-amber-950 hover:bg-amber-600/20"
          >
            <X className="h-4 w-4 mr-1" />
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
};
